import React, { useEffect, useState, createElement } from 'react';
import { ArrowRightIcon, CheckIcon, PaletteIcon, CodeIcon, SearchIcon, ServerIcon, UserIcon, BuildingIcon, SettingsIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { FinalCtaSection } from '../components/FinalCtaSection';
export function KostenWebsiteLatenMakenPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Wat kost een website laten maken? | Prijzen 2026 | Noord';
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'Wat kost een website laten maken in 2026? Prijzen vanaf €500 voor ZZP tot €8.000+ voor maatwerk. Transparant overzicht van kosten per type website.';
    // Open Graph meta tags
    const ogTags: Record<string, string> = {
      'og:title': 'Wat kost een website laten maken? | Prijzen 2026 | Noord',
      'og:description': 'Wat kost een website laten maken in 2026? Prijzen vanaf €500 voor ZZP tot €8.000+ voor maatwerk.',
      'og:type': 'article',
      'og:url': 'https://noorddigital.nl/kosten-website-laten-maken',
      'og:site_name': 'Noord Digital',
      'og:locale': 'nl_NL'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, []);
  const scrollToContact = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('contact')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const faqs = [{
    q: 'Wat kost een website gemiddeld?',
    a: 'De gemiddelde kosten voor een professionele bedrijfswebsite liggen tussen de €1.500 en €3.500. Een eenvoudige starterswebsite kan al vanaf €500, terwijl maatwerk websites met geavanceerde functies €3.000 tot €8.000 of meer kunnen kosten.'
  }, {
    q: 'Hoeveel kost een WordPress website?',
    a: "Een WordPress website kost gemiddeld tussen de €1.000 en €4.000, afhankelijk van het aantal pagina's, het design en de gewenste functionaliteiten. WordPress is een uitstekende keuze vanwege de flexibiliteit en het gebruiksgemak."
  }, {
    q: 'Zijn er maandelijkse kosten?',
    a: 'Ja, naast de eenmalige bouwkosten zijn er maandelijkse kosten voor hosting, onderhoud en eventueel doorlopende SEO. Bij Noord Digital bieden wij transparante onderhoudspakketten zodat je precies weet waar je aan toe bent.'
  }, {
    q: 'Kan een website goedkoper?',
    a: 'Er zijn goedkopere opties zoals doe-het-zelf bouwers (Wix, Squarespace), maar deze bieden minder flexibiliteit, slechtere SEO-mogelijkheden en een minder professionele uitstraling. Een professionele website is een investering die zich terugverdient door meer klanten en aanvragen.'
  }];
  return <main className="pt-16 lg:pt-[72px]" data-id="element-1378">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-1379">
        <div className="absolute inset-0 pointer-events-none" data-id="element-1380">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-1381" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-1382" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-1383" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1384">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-sm mb-8 animate-fade-in-up" data-id="element-1385">
            <a href="#/" className="text-navy-400 hover:text-white transition-colors" data-id="element-1386">
              Home
            </a>
            <span className="text-navy-500" data-id="element-1387">/</span>
            <a href="#/kennisbank" className="text-navy-400 hover:text-white transition-colors" data-id="element-1388">
              Kennisbank
            </a>
            <span className="text-navy-500" data-id="element-1389">/</span>
            <span className="text-accent-light font-medium" data-id="element-1390">
              Kosten website
            </span>
          </nav>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up" data-id="element-1391">
            Wat kost een website laten maken?
          </h1>
          <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mx-auto mb-10 animate-fade-in-up-delay-1" data-id="element-1392">
            Een overzicht van de kosten voor een professionele bedrijfswebsite.
            Van starterswebsite tot volledig maatwerk.
          </p>
          <div className="animate-fade-in-up-delay-2" data-id="element-1393">
            <a href="#contact" onClick={scrollToContact} className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-lg" data-id="element-1394">
              Vraag een prijsindicatie aan
              <ArrowRightIcon className="w-5 h-5" data-id="element-1395" />
            </a>
          </div>
        </div>
      </section>

      {/* Gemiddelde kosten */}
      <section className="py-24 bg-surface-muted" data-id="element-1396">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1397">
          <div className="text-center mb-16" data-id="element-1398">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1399">
              Gemiddelde kosten per type website
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-1400">
              De prijs van een website hangt af van het type en de complexiteit.
              Hieronder een indicatie.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1401">
            {/* Starter website */}
            <div className="bg-white rounded-card shadow-card p-8 flex flex-col" data-id="element-1402">
              <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1403">
                Starter website
              </h3>
              <div className="text-3xl font-extrabold text-navy-900 mb-4" data-id="element-1404">
                Vanaf €500
              </div>
              <p className="text-slate-600 mb-6 flex-grow" data-id="element-1405">
                Een eenvoudige website met de basis. Ideaal voor zzp'ers die
                snel online willen.
              </p>
              <ul className="space-y-3 mb-8" data-id="element-1406">
                <li className="flex items-start gap-3" data-id="element-1407">
                  <CheckIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1408" />
                  <span className="text-slate-700 text-sm" data-id="element-1409">1-3 pagina's</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1410">
                  <CheckIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1411" />
                  <span className="text-slate-700 text-sm" data-id="element-1412">
                    Responsive design
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1413">
                  <CheckIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1414" />
                  <span className="text-slate-700 text-sm" data-id="element-1415">
                    Contactformulier
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1416">
                  <CheckIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1417" />
                  <span className="text-slate-700 text-sm" data-id="element-1418">Basis SEO</span>
                </li>
              </ul>
            </div>

            {/* Bedrijfswebsite */}
            <div className="bg-white rounded-card shadow-card p-8 flex flex-col relative border-2 border-accent transform md:-translate-y-2" data-id="element-1419">
              <div className="absolute -top-3 right-6 bg-accent text-white text-xs font-bold px-3 py-1 rounded-full" data-id="element-1420">
                Meest gekozen
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1421">
                Bedrijfswebsite
              </h3>
              <div className="text-3xl font-extrabold text-navy-900 mb-4" data-id="element-1422">
                Vanaf €1.500
              </div>
              <p className="text-slate-600 mb-6 flex-grow" data-id="element-1423">
                Een professionele website die jouw bedrijf goed presenteert en
                klanten aantrekt.
              </p>
              <ul className="space-y-3 mb-8" data-id="element-1424">
                <li className="flex items-start gap-3" data-id="element-1425">
                  <CheckIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1426" />
                  <span className="text-slate-700 text-sm" data-id="element-1427">5-10 pagina's</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1428">
                  <CheckIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1429" />
                  <span className="text-slate-700 text-sm" data-id="element-1430">
                    SEO-optimalisatie
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1431">
                  <CheckIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1432" />
                  <span className="text-slate-700 text-sm" data-id="element-1433">
                    CMS (WordPress)
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1434">
                  <CheckIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1435" />
                  <span className="text-slate-700 text-sm" data-id="element-1436">
                    Google Analytics
                  </span>
                </li>
              </ul>
            </div>

            {/* Maatwerk website */}
            <div className="bg-white rounded-card shadow-card p-8 flex flex-col" data-id="element-1437">
              <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1438">
                Maatwerk website
              </h3>
              <div className="text-3xl font-extrabold text-navy-900 mb-4" data-id="element-1439">
                Vanaf €3.000
              </div>
              <p className="text-slate-600 mb-6 flex-grow" data-id="element-1440">
                Een volledig op maat gemaakte website met geavanceerde
                functionaliteiten.
              </p>
              <ul className="space-y-3 mb-8" data-id="element-1441">
                <li className="flex items-start gap-3" data-id="element-1442">
                  <CheckIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1443" />
                  <span className="text-slate-700 text-sm" data-id="element-1444">
                    Onbeperkt pagina's
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1445">
                  <CheckIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1446" />
                  <span className="text-slate-700 text-sm" data-id="element-1447">
                    Koppelingen & integraties
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1448">
                  <CheckIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1449" />
                  <span className="text-slate-700 text-sm" data-id="element-1450">
                    Geavanceerde SEO
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1451">
                  <CheckIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1452" />
                  <span className="text-slate-700 text-sm" data-id="element-1453">
                    Maatwerkfuncties
                  </span>
                </li>
              </ul>
            </div>

            {/* Webshop */}
            <div className="bg-white rounded-card shadow-card p-8 flex flex-col" data-id="element-1454">
              <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1455">Webshop</h3>
              <div className="text-3xl font-extrabold text-navy-900 mb-4" data-id="element-1456">
                Vanaf €2.500
              </div>
              <p className="text-slate-600 mb-6 flex-grow" data-id="element-1457">
                Een complete webshop met productbeheer, betaalsysteem en
                voorraadbeheer.
              </p>
              <ul className="space-y-3 mb-8" data-id="element-1458">
                <li className="flex items-start gap-3" data-id="element-1459">
                  <CheckIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1460" />
                  <span className="text-slate-700 text-sm" data-id="element-1461">WooCommerce</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1462">
                  <CheckIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1463" />
                  <span className="text-slate-700 text-sm" data-id="element-1464">iDEAL / Mollie</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1465">
                  <CheckIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1466" />
                  <span className="text-slate-700 text-sm" data-id="element-1467">Productbeheer</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1468">
                  <CheckIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1469" />
                  <span className="text-slate-700 text-sm" data-id="element-1470">Verzendopties</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Waaruit bestaat de prijs */}
      <section className="py-24 bg-white" data-id="element-1471">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1472">
          <div className="text-center mb-16" data-id="element-1473">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1474">
              Waaruit bestaat de prijs van een website?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-1475">
              Een website laten maken omvat meer dan alleen design. Dit zijn de
              onderdelen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1476">
            <div className="bg-surface-muted p-8 rounded-card border border-navy-100" data-id="element-1477">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1478">
                <PaletteIcon className="w-6 h-6 text-accent" data-id="element-1479" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1480">Design</h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1481">
                Het visuele ontwerp van je website, afgestemd op jouw huisstijl
                en doelgroep.
              </p>
            </div>
            <div className="bg-surface-muted p-8 rounded-card border border-navy-100" data-id="element-1482">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1483">
                <CodeIcon className="w-6 h-6 text-accent" data-id="element-1484" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1485">
                Ontwikkeling
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1486">
                Het technisch bouwen van de website, inclusief responsiveness en
                snelheid.
              </p>
            </div>
            <div className="bg-surface-muted p-8 rounded-card border border-navy-100" data-id="element-1487">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1488">
                <SearchIcon className="w-6 h-6 text-accent" data-id="element-1489" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1490">
                SEO basis
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1491">
                Technische SEO, meta-tags, sitemap en snelheidsoptimalisatie
                voor Google.
              </p>
            </div>
            <div className="bg-surface-muted p-8 rounded-card border border-navy-100" data-id="element-1492">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1493">
                <ServerIcon className="w-6 h-6 text-accent" data-id="element-1494" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1495">
                Hosting en onderhoud
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1496">
                Betrouwbare hosting, updates, backups en technisch beheer na
                oplevering.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Kosten voorbeeld */}
      <section className="py-24 bg-surface-muted" data-id="element-1497">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1498">
          <div className="text-center mb-16" data-id="element-1499">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1500">
              Voorbeelden uit de praktijk
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-1501">
              Wat kost een website voor jouw type bedrijf? Drie veelvoorkomende
              scenario's.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1502">
            <div className="bg-white rounded-card shadow-card p-8" data-id="element-1503">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1504">
                <UserIcon className="w-6 h-6 text-accent" data-id="element-1505" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1506">
                Website voor zzp'er
              </h3>
              <div className="text-2xl font-extrabold text-accent mb-6" data-id="element-1507">
                €500 – €1.200
              </div>
              <ul className="space-y-3 mb-8" data-id="element-1508">
                <li className="flex items-start gap-3" data-id="element-1509">
                  <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-1510" />
                  <span className="text-slate-700 text-sm" data-id="element-1511">
                    One-pager of 3 pagina's
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1512">
                  <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-1513" />
                  <span className="text-slate-700 text-sm" data-id="element-1514">
                    Contactformulier
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1515">
                  <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-1516" />
                  <span className="text-slate-700 text-sm" data-id="element-1517">Basis SEO</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1518">
                  <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-1519" />
                  <span className="text-slate-700 text-sm" data-id="element-1520">
                    Responsive design
                  </span>
                </li>
              </ul>
              <div className="p-4 bg-navy-50 rounded-xl text-sm text-navy-700 italic border-l-4 border-accent" data-id="element-1521">
                "Perfect voor freelancers en starters die snel professioneel
                online willen."
              </div>
            </div>

            <div className="bg-white rounded-card shadow-card p-8" data-id="element-1522">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1523">
                <BuildingIcon className="w-6 h-6 text-accent" data-id="element-1524" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1525">
                Website voor mkb
              </h3>
              <div className="text-2xl font-extrabold text-accent mb-6" data-id="element-1526">
                €1.500 – €3.500
              </div>
              <ul className="space-y-3 mb-8" data-id="element-1527">
                <li className="flex items-start gap-3" data-id="element-1528">
                  <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-1529" />
                  <span className="text-slate-700 text-sm" data-id="element-1530">5-15 pagina's</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1531">
                  <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-1532" />
                  <span className="text-slate-700 text-sm" data-id="element-1533">WordPress CMS</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1534">
                  <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-1535" />
                  <span className="text-slate-700 text-sm" data-id="element-1536">
                    SEO-optimalisatie
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1537">
                  <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-1538" />
                  <span className="text-slate-700 text-sm" data-id="element-1539">
                    Google Analytics & Blog
                  </span>
                </li>
              </ul>
              <div className="p-4 bg-navy-50 rounded-xl text-sm text-navy-700 italic border-l-4 border-accent" data-id="element-1540">
                "De meest gekozen optie voor groeiende bedrijven in Alkmaar en
                omgeving."
              </div>
            </div>

            <div className="bg-white rounded-card shadow-card p-8" data-id="element-1541">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1542">
                <SettingsIcon className="w-6 h-6 text-accent" data-id="element-1543" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1544">
                Maatwerk website
              </h3>
              <div className="text-2xl font-extrabold text-accent mb-6" data-id="element-1545">
                €3.000 – €8.000+
              </div>
              <ul className="space-y-3 mb-8" data-id="element-1546">
                <li className="flex items-start gap-3" data-id="element-1547">
                  <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-1548" />
                  <span className="text-slate-700 text-sm" data-id="element-1549">
                    Onbeperkt pagina's
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1550">
                  <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-1551" />
                  <span className="text-slate-700 text-sm" data-id="element-1552">
                    Koppelingen (CRM, boekingen)
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1553">
                  <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-1554" />
                  <span className="text-slate-700 text-sm" data-id="element-1555">
                    Geavanceerde functies
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1556">
                  <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-1557" />
                  <span className="text-slate-700 text-sm" data-id="element-1558">
                    Doorlopend onderhoud
                  </span>
                </li>
              </ul>
              <div className="p-4 bg-navy-50 rounded-xl text-sm text-navy-700 italic border-l-4 border-accent" data-id="element-1559">
                "Voor bedrijven die specifieke functionaliteiten en integraties
                nodig hebben."
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Wat bepaalt de prijs */}
      <section className="py-24 bg-navy-900 relative overflow-hidden" data-id="element-1560">
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }} data-id="element-1561" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1562">
          <div className="text-center mb-16" data-id="element-1563">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-1564">
              Wat bepaalt de prijs van jouw website?
            </h2>
            <p className="text-lg text-navy-300 max-w-2xl mx-auto" data-id="element-1565">
              Verschillende factoren beïnvloeden de uiteindelijke kosten.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1566">
            <div className="bg-white/[0.06] border border-white/10 rounded-2xl p-6" data-id="element-1567">
              <h3 className="text-xl font-bold text-white mb-3" data-id="element-1568">
                Aantal pagina's
              </h3>
              <p className="text-navy-300 leading-relaxed" data-id="element-1569">
                Meer pagina's betekent meer content en design. Een one-pager is
                goedkoper dan een website met 20 pagina's.
              </p>
            </div>
            <div className="bg-white/[0.06] border border-white/10 rounded-2xl p-6" data-id="element-1570">
              <h3 className="text-xl font-bold text-white mb-3" data-id="element-1571">
                Functionaliteiten
              </h3>
              <p className="text-navy-300 leading-relaxed" data-id="element-1572">
                Contactformulieren, boekingssystemen of klantportalen verhogen
                de complexiteit en kosten.
              </p>
            </div>
            <div className="bg-white/[0.06] border border-white/10 rounded-2xl p-6" data-id="element-1573">
              <h3 className="text-xl font-bold text-white mb-3" data-id="element-1574">SEO</h3>
              <p className="text-navy-300 leading-relaxed" data-id="element-1575">
                Basis SEO is standaard inbegrepen. Uitgebreide SEO-strategie en
                contentcreatie is een meerwaarde.
              </p>
            </div>
            <div className="bg-white/[0.06] border border-white/10 rounded-2xl p-6" data-id="element-1576">
              <h3 className="text-xl font-bold text-white mb-3" data-id="element-1577">Integraties</h3>
              <p className="text-navy-300 leading-relaxed" data-id="element-1578">
                Koppelingen met externe systemen zoals CRM, boekhoudpakketten of
                betaalsystemen.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Gerelateerde pagina's */}
      <section className="py-16 bg-white border-b border-navy-100" data-id="element-1579">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1580">
          <h3 className="text-lg font-semibold text-navy-900 mb-6" data-id="element-1581">
            Ontdek meer:
          </h3>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-1582">
            <a href="#/website-laten-maken" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-1583">
              Website laten maken
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-1584">•</span>
            <a href="#/website-laten-maken-checklist" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-1585">
              Checklist voor je nieuwe website
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-1586">•</span>
            <a href="#/website-laten-maken-of-zelf-bouwen" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-1587">
              Zelf bouwen of uitbesteden?
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-1588">•</span>
            <a href="#/" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-1589">
              Terug naar homepage
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-surface-muted" data-id="element-1590">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1591">
          <div className="text-center mb-16" data-id="element-1592">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1593">
              Veelgestelde vragen over websitekosten
            </h2>
          </div>

          <div className="space-y-4" data-id="element-1594">
            {faqs.map((faq, index) => <div key={index} className="bg-white rounded-xl shadow-soft overflow-hidden" data-id="element-1595">
                <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-1596">
                  <span className="font-bold text-navy-900" data-id="element-1597">{faq.q}</span>
                  {openFaq === index ? <ChevronUpIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1598" /> : <ChevronDownIcon className="w-5 h-5 text-navy-400 flex-shrink-0" data-id="element-1599" />}
                </button>
                {openFaq === index && <div className="px-6 pb-5 text-slate-600 leading-relaxed" data-id="element-1600">
                    {faq.a}
                  </div>}
              </div>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-1601" />
    </main>;
}